import { supabase } from './supabase';
import { PlanCode } from './planCatalog';

/**
 * Permisos del usuario según su plan
 */
export interface UserPermissions {
  plan: PlanCode | 'guest';
  analyses_today: number;
  max_analyses: number;
  analyses_remaining: number;
  max_leagues: number;
  max_markets: number;
  can_view_ai_insights: boolean;
  can_view_value_picks: boolean;
  can_view_personalized: boolean;
  has_priority_support: boolean;
  has_early_access: boolean;
  favorite_leagues: number[];
}

/**
 * Configuración de límites por plan
 */
export const PLAN_LIMITS = {
  guest: {
    max_analyses: 0,
    max_leagues: 0,
    max_markets: 3,
    can_view_ai_insights: false,
    can_view_value_picks: false,
    can_view_personalized: false,
  },
  free: {
    max_analyses: 1,
    max_leagues: 999,
    max_markets: 5,
    can_view_ai_insights: false,
    can_view_value_picks: false,
    can_view_personalized: false,
  },
  standard: {
    max_analyses: 12,
    max_leagues: 8,
    max_markets: 15,
    can_view_ai_insights: true,
    can_view_value_picks: false,
    can_view_personalized: false,
  },
  pro: {
    max_analyses: 20,
    max_leagues: 999,
    max_markets: 50,
    can_view_ai_insights: true,
    can_view_value_picks: true,
    can_view_personalized: false,
  },
  experto: {
    max_analyses: 999999,
    max_leagues: 999,
    max_markets: 50,
    can_view_ai_insights: true,
    can_view_value_picks: true,
    can_view_personalized: true,
  },
} as const;

/**
 * Mercados disponibles por nivel
 */
export const MARKETS_BY_TIER = {
  // Mercados básicos (Free - 5 mercados)
  basic: [
    'winner',           // Ganador del partido
    'over_1_5',         // Más de 1.5 goles
    'over_2_5',         // Más de 2.5 goles
    'btts',             // Ambos marcan
    'double_chance',    // Doble oportunidad
  ],
  // Mercados estándar (Standard - 15 mercados)
  standard: [
    'winner',
    'over_0_5', 'over_1_5', 'over_2_5', 'over_3_5',
    'btts',
    'double_chance_1x', 'double_chance_12', 'double_chance_x2',
    'first_half_winner',
    'home_over_0_5', 'home_over_1_5',
    'away_over_0_5', 'away_over_1_5',
    'corners_over_9',
  ],
  // Mercados premium (Pro/Experto - 50+ mercados)
  premium: [
    'winner',
    'over_0_5', 'over_1_5', 'over_2_5', 'over_3_5', 'over_4_5',
    'btts',
    'double_chance_1x', 'double_chance_12', 'double_chance_x2',
    'first_half_winner', 'first_half_draw', 'first_half_over_0_5', 'first_half_over_1_5',
    'home_over_0_5', 'home_over_1_5', 'home_over_2_5', 'home_over_3_5',
    'away_over_0_5', 'away_over_1_5', 'away_over_2_5', 'away_over_3_5',
    'corners_over_9', 'corners_over_10', 'corners_over_10_5', 'corners_over_11',
    'cards_over_2_5', 'cards_over_3_5', 'cards_over_4_5',
    'first_scorer_home', 'first_scorer_away', 'first_scorer_none',
    'exact_score_1_0', 'exact_score_2_0', 'exact_score_2_1', 'exact_score_1_1',
    'exact_score_0_0', 'exact_score_0_1', 'exact_score_0_2', 'exact_score_1_2',
    // ... más mercados de resultados exactos
  ],
};

/**
 * Ligas principales para plan Standard (8 ligas)
 */
export const STANDARD_LEAGUES = [
  39,   // Premier League
  140,  // La Liga
  78,   // Bundesliga
  135,  // Serie A
  61,   // Ligue 1
  2,    // Champions League
  3,    // Europa League
  848,  // Conference League
];

/**
 * Servicio de permisos de plan
 */
export class PlanPermissionsService {
  /**
   * Obtener permisos del usuario actual
   */
  static async getUserPermissions(): Promise<UserPermissions> {
    const { data, error } = await supabase.rpc('get_user_permissions');

    if (error) {
      // Retornar permisos de guest por defecto
      return {
        plan: 'guest',
        analyses_today: 0,
        max_analyses: 0,
        analyses_remaining: 0,
        max_leagues: 0,
        max_markets: 3,
        can_view_ai_insights: false,
        can_view_value_picks: false,
        can_view_personalized: false,
        has_priority_support: false,
        has_early_access: false,
        favorite_leagues: [],
      };
    }

    return data as UserPermissions;
  }

  /**
   * Registrar que el usuario vio un análisis
   */
  static async incrementAnalysisCount(fixtureId: number): Promise<{
    success: boolean;
    error?: string;
    current_count?: number;
    max_allowed?: number;
    already_viewed?: boolean;
  }> {
    const { data, error } = await supabase.rpc('increment_analysis_count', {
      p_fixture_id: fixtureId,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data;
  }

  /**
   * Verificar si el usuario puede acceder a una liga
   */
  static async canAccessLeague(leagueId: number): Promise<boolean> {
    const { data, error } = await supabase.rpc('can_access_league', {
      p_league_id: leagueId,
    });

    if (error) {
      return false;
    }

    return data as boolean;
  }

  /**
   * Agregar liga a favoritos
   */
  static async addFavoriteLeague(leagueId: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    const { data, error } = await supabase.rpc('add_favorite_league', {
      p_league_id: leagueId,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data;
  }

  /**
   * Remover liga de favoritos
   */
  static async removeFavoriteLeague(leagueId: number): Promise<{
    success: boolean;
    error?: string;
  }> {
    const { data, error } = await supabase.rpc('remove_favorite_league', {
      p_league_id: leagueId,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data;
  }

  /**
   * Obtener mercados disponibles según el plan
   */
  static getAvailableMarkets(plan: PlanCode | 'guest'): string[] {
    switch (plan) {
      case 'pro':
      case 'experto':
        return MARKETS_BY_TIER.premium;
      case 'standard':
        return MARKETS_BY_TIER.standard;
      case 'free':
        return MARKETS_BY_TIER.basic;
      default:
        return MARKETS_BY_TIER.basic.slice(0, 3); // Solo 3 para guests
    }
  }

  /**
   * Verificar si un mercado específico está disponible
   */
  static isMarketAvailable(plan: PlanCode | 'guest', marketKey: string): boolean {
    const availableMarkets = this.getAvailableMarkets(plan);
    return availableMarkets.includes(marketKey);
  }

  /**
   * Verificar si el usuario puede ver más análisis hoy
   */
  static canViewMoreAnalyses(permissions: UserPermissions): boolean {
    return permissions.analyses_remaining > 0;
  }

  /**
   * Obtener mensaje de upgrade según el límite alcanzado
   */
  static getUpgradeMessage(limitType: 'analyses' | 'leagues' | 'markets', currentPlan: PlanCode | 'guest'): string {
    const messages = {
      analyses: {
        guest: 'Regístrate gratis para ver predicciones',
        free: 'Actualiza a Standard para 12 análisis diarios',
        standard: 'Actualiza a Pro para 20 análisis diarios',
        pro: 'Actualiza a Experto para análisis ilimitados',
        experto: '',
      },
      leagues: {
        guest: 'Regístrate para acceder a más ligas',
        free: '',
        standard: 'Actualiza a Pro para acceso a todas las ligas',
        pro: '',
        experto: '',
      },
      markets: {
        guest: 'Regístrate para ver más mercados',
        free: 'Actualiza a Standard para 15 mercados por partido',
        standard: 'Actualiza a Pro para 50+ mercados por partido',
        pro: '',
        experto: '',
      },
    };

    return messages[limitType][currentPlan] || '';
  }

  /**
   * Registrar que el usuario desbloqueó un parlay
   * Deduce tokens según el número de apuestas en el parlay
   */
  static async unlockParlay(parlayId: string, tokensRequired: number): Promise<{
    success: boolean;
    error?: string;
    current_count?: number;
    max_allowed?: number;
    already_unlocked?: boolean;
  }> {
    const { data, error } = await supabase.rpc('unlock_parlay', {
      p_parlay_id: parlayId,
      p_tokens_required: tokensRequired,
    });

    if (error) {
      return { success: false, error: error.message };
    }

    return data;
  }

  /**
   * Verificar si un parlay ya fue desbloqueado por el usuario
   */
  static async isParlayUnlocked(parlayId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc('is_parlay_unlocked', {
      p_parlay_id: parlayId,
    });

    if (error) {
      return false;
    }

    return data as boolean;
  }

  /**
   * Obtener todos los parlays desbloqueados por el usuario hoy
   */
  static async getUnlockedParlays(): Promise<string[]> {
    const { data, error } = await supabase.rpc('get_unlocked_parlays');

    if (error) {
      return [];
    }

    return (data as any[]).map(item => item.parlay_id);
  }
}
